import React from "react";
import { useTranslation } from "react-i18next";

import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid2 as Grid,
  InputAdornment,
  TextField,
  Typography
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { Select } from "components/forms";
import dayjs from "dayjs";
import { FormikProps } from "formik";
import { useDateFormatSelect } from "store";
import toIsoMidday from "utils/to-iso-midday";

import { FloatTypeSelect } from "./form-fields/FloatTypeSelect";
import { RateFixingTypeSelect } from "./form-fields/RateFixingTypeSelect";
import { ReferenceIndexSelect } from "./form-fields/ReferenceIndexSelect";
import { FormValues } from "./schemas";

interface Props {
  formik: FormikProps<FormValues>;
  updateIndexValue: (index: string, date: string | null, type: "principal" | "interest") => Promise<void>;
  size?: "small" | "large";
  disabledFields?: string[];
  hiddenFields?: string[];
}

export const AmortizationDrawerInterestSection: React.FC<Props> = ({
  formik,
  updateIndexValue,
  size = "small",
  disabledFields = [],
  hiddenFields = []
}) => {
  const { t } = useTranslation("debt");
  const dateFormat = useDateFormatSelect();
  const gridSize = size === "large" ? 3 : 4;
  const isDisabled = !formik.values.base_rate && !formik.values.interest_reference_index;
  return (
    <Box>
      <Box sx={{ mb: 5 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          {t("interestSection.title")}
        </Typography>
        <Divider />
      </Box>
      <Grid container spacing={5}>
        <Grid size={gridSize} sx={{ display: hiddenFields.includes("base_rate") ? "none" : "block" }}>
          <TextField
            fullWidth
            disabled={disabledFields.includes("base_rate")}
            type="number"
            name="base_rate"
            label={t("interestSection.baseRate")}
            value={formik.values.base_rate}
            onChange={formik.handleChange}
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">%</InputAdornment>
              }
            }}
            error={formik.touched.base_rate && Boolean(formik.errors.base_rate)}
            helperText={formik.touched.base_rate && formik.errors.base_rate}
          />
        </Grid>
        <Grid size={gridSize} sx={{ display: hiddenFields.includes("float_type") ? "none" : "block" }}>
          <FloatTypeSelect
            name="float_type"
            label={t("interestSection.interestType")}
            formik={formik}
            disabled={disabledFields.includes("float_type")}
            size={size === "large" ? "medium" : "small"}
            onChange={(e) => {
              formik.handleChange(e);
              if (e.target.value === "fixed") {
                formik.setFieldValue("interest_reference_index", null);
                formik.setFieldValue("interest_rate_fixing", null);
              }
            }}
          />
        </Grid>
        <Grid size={gridSize} sx={{ display: hiddenFields.includes("interest_rate_fixing") ? "none" : "block" }}>
          <RateFixingTypeSelect
            name="interest_rate_fixing"
            label={t("interestSection.rateFixing")}
            formik={formik}
            disabled={disabledFields.includes("interest_rate_fixing") || formik.values.float_type !== "spread"}
            size={size === "large" ? "medium" : "small"}
          />
        </Grid>
        <Grid size={gridSize} sx={{ display: hiddenFields.includes("interest_reference_index") ? "none" : "block" }}>
          <ReferenceIndexSelect
            name="interest_reference_index"
            formik={formik}
            label={t("interestSection.index")}
            disabled={disabledFields.includes("interest_reference_index") || formik.values.float_type === "fixed"}
            size={size === "large" ? "medium" : "small"}
            onIndexChange={(index) => updateIndexValue(index, formik.values.interest_reference_day || null, "interest")}
            onIndexClear={() => {
              formik.setFieldValue("interest_link_percent", 0);
              formik.setFieldValue("interest_index_value", null);
            }}
          />
        </Grid>
        <Grid size={gridSize} sx={{ display: hiddenFields.includes("interest_index_value") ? "none" : "block" }}>
          <TextField
            fullWidth
            disabled
            name="interest_index_value"
            label={t("interestSection.indexAtReferenceValue")}
            value={formik.values.interest_index_value || ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid size={gridSize} sx={{ display: hiddenFields.includes("interest_link_percent") ? "none" : "block" }}>
          <TextField
            fullWidth
            disabled={
              isDisabled || disabledFields.includes("interest_link_percent") || formik.values.float_type !== "linked"
            }
            type="number"
            name="interest_link_percent"
            label={t("interestSection.percentage")}
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">%</InputAdornment>
              }
            }}
            value={formik.values.interest_link_percent}
            onChange={formik.handleChange}
            error={formik.touched.interest_link_percent && Boolean(formik.errors.interest_link_percent)}
            helperText={formik.touched.interest_link_percent && formik.errors.interest_link_percent}
          />
        </Grid>
        <Grid size={gridSize} sx={{ display: hiddenFields.includes("interest_reference_day") ? "none" : "block" }}>
          <DesktopDatePicker
            label={t("interestSection.referenceDate")}
            disabled={
              isDisabled || disabledFields.includes("interest_reference_day") || formik.values.float_type === "fixed"
            }
            format={dateFormat}
            views={["year", "month", "day"]}
            value={formik.values.interest_reference_day ? dayjs(formik.values.interest_reference_day) : null}
            onChange={(newValue) => {
              formik.setFieldValue("interest_reference_day", newValue ? toIsoMidday(newValue) : "");
              if (formik.values.interest_reference_index) {
                updateIndexValue(
                  formik.values.interest_reference_index,
                  newValue ? toIsoMidday(newValue) : null,
                  "interest"
                );
              }
            }}
            sx={{ width: "100%" }}
            slotProps={{
              textField: {
                error: formik.touched.interest_reference_day && Boolean(formik.errors.interest_reference_day),
                helperText: formik.touched.interest_reference_day && formik.errors.interest_reference_day
              }
            }}
          />
        </Grid>
        <Grid size={gridSize} sx={{ display: hiddenFields.includes("interest_first_payment_date") ? "none" : "block" }}>
          <DesktopDatePicker
            disabled={isDisabled || disabledFields.includes("interest_first_payment_date")}
            label={t("interestSection.paymentDate")}
            format={dateFormat}
            views={["year", "month", "day"]}
            value={formik.values.interest_first_payment_date ? dayjs(formik.values.interest_first_payment_date) : null}
            onChange={(newValue) =>
              formik.setFieldValue("interest_first_payment_date", newValue ? toIsoMidday(newValue) : "")
            }
            sx={{ width: "100%" }}
            slotProps={{
              textField: {
                error: formik.touched.interest_first_payment_date && Boolean(formik.errors.interest_first_payment_date),
                helperText: formik.touched.interest_first_payment_date && formik.errors.interest_first_payment_date
              }
            }}
          />
        </Grid>
        <Grid size={gridSize} sx={{ display: hiddenFields.includes("interest_frequency") ? "none" : "block" }}>
          <Select
            disabled={isDisabled || disabledFields.includes("interest_frequency")}
            name="interest_frequency"
            label={t("interestSection.frequency")}
            value={formik.values.interest_frequency}
            variant="outlined"
            fullWidth
            items={[
              { key: "month", value: "month", text: t("common.monthly") },
              { key: "quarter", value: "quarter", text: t("common.quarterly") },
              { key: "semi-annual", value: "semi-annual", text: t("common.semiAnnual") },
              { key: "year", value: "year", text: t("common.annual") }
            ]}
            error={formik.touched.interest_frequency && Boolean(formik.errors.interest_frequency)}
            helperText={formik.touched.interest_frequency ? formik.errors.interest_frequency : undefined}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid size={gridSize} sx={{ display: hiddenFields.includes("floor_rate") ? "none" : "block" }}>
          <TextField
            fullWidth
            disabled={isDisabled || disabledFields.includes("floor_rate")}
            type="number"
            name="floor_rate"
            label={t("interestSection.floorRate")}
            value={formik.values.floor_rate || ""}
            onChange={formik.handleChange}
            error={formik.touched.floor_rate && Boolean(formik.errors.floor_rate)}
            helperText={formik.touched.floor_rate && formik.errors.floor_rate}
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">%</InputAdornment>
              }
            }}
          />
        </Grid>
        <Grid size={gridSize} sx={{ display: hiddenFields.includes("ceiling_rate") ? "none" : "block" }}>
          <TextField
            fullWidth
            disabled={isDisabled || disabledFields.includes("ceiling_rate")}
            type="number"
            name="ceiling_rate"
            label={t("interestSection.ceilingRate")}
            value={formik.values.ceiling_rate || ""}
            onChange={formik.handleChange}
            error={formik.touched.ceiling_rate && Boolean(formik.errors.ceiling_rate)}
            helperText={formik.touched.ceiling_rate && formik.errors.ceiling_rate}
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">%</InputAdornment>
              }
            }}
          />
        </Grid>
        <Grid size={gridSize} sx={{ display: hiddenFields.includes("day_count_convention") ? "none" : "block" }}>
          <Select
            disabled={disabledFields.includes("day_count_convention")}
            name="day_count_convention"
            label={t("interestSection.dayCountConvention")}
            value={formik.values.day_count_convention}
            variant="outlined"
            fullWidth
            items={[
              { key: "actual_actual", value: "actual_actual", text: t("accrual.actualActual") },
              { key: "actual_360", value: "actual_360", text: t("accrual.actual360") },
              { key: "actual_365", value: "actual_365", text: t("accrual.actual365") },
              { key: "30_360", value: "30_360", text: t("accrual.thirty360") }
            ]}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid size={gridSize} sx={{ display: hiddenFields.includes("loan_reset_date") ? "none" : "block" }}>
          <DesktopDatePicker
            label={t("interestSection.loanResetDate")}
            disabled={
              isDisabled || disabledFields.includes("loan_reset_date") || formik.values.float_type === "fixed"
            }
            format={dateFormat}
            views={["year", "month", "day"]}
            value={formik.values.loan_reset_date ? dayjs(formik.values.loan_reset_date) : null}
            onChange={(newValue) => formik.setFieldValue("loan_reset_date", newValue ? toIsoMidday(newValue) : "")}
            sx={{ width: "100%" }}
            slotProps={{
              textField: {
                error: formik.touched.loan_reset_date && Boolean(formik.errors.loan_reset_date),
                helperText: formik.touched.loan_reset_date && formik.errors.loan_reset_date
              }
            }}
          />
        </Grid>
        <Grid size={gridSize} sx={{ display: hiddenFields.includes("loan_reset_date_offset") ? "none" : "block" }}>
          <TextField
            fullWidth
            disabled={
              isDisabled || disabledFields.includes("loan_reset_date_offset") || formik.values.float_type === "fixed"
            }
            type="number"
            name="loan_reset_date_offset"
            label="Reference Day Offset"
            value={formik.values.loan_reset_date_offset ?? ""}
            onChange={formik.handleChange}
            error={formik.touched.loan_reset_date_offset && Boolean(formik.errors.loan_reset_date_offset)}
            helperText={formik.touched.loan_reset_date_offset && formik.errors.loan_reset_date_offset}
            slotProps={{ input: { inputProps: { step: 1 } } }}
          />
        </Grid>
        <Grid size={gridSize} sx={{ display: hiddenFields.includes("loan_reset_frequency") ? "none" : "block" }}>
          <Select
            disabled={
              isDisabled || disabledFields.includes("loan_reset_frequency") || formik.values.float_type === "fixed"
            }
            name="loan_reset_frequency"
            label={t("interestSection.loanResetFrequency")}
            value={formik.values.loan_reset_frequency}
            variant="outlined"
            fullWidth
            items={[
              { key: "month", value: "month", text: t("common.monthly") },
              { key: "quarter", value: "quarter", text: t("common.quarterly") },
              { key: "semi-annual", value: "semi-annual", text: t("common.semiAnnual") },
              { key: "year", value: "year", text: t("common.annual") }
            ]}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid size={gridSize} sx={{ display: hiddenFields.includes("commission_rate") ? "none" : "block" }}>
          <TextField
            fullWidth
            disabled={isDisabled || disabledFields.includes("commission_rate")}
            type="number"
            name="commission_rate"
            label={t("interestSection.commission")}
            value={formik.values.commission_rate || ""}
            onChange={formik.handleChange}
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">%</InputAdornment>
              }
            }}
            error={formik.touched.commission_rate && Boolean(formik.errors.commission_rate)}
            helperText={formik.touched.commission_rate && formik.errors.commission_rate}
          />
        </Grid>
        <Grid
          size={gridSize}
          sx={{ display: hiddenFields.includes("interest_on_pik_principal") ? "none" : "flex", alignItems: "center" }}
        >
          <FormControlLabel
            control={
              <Checkbox
                name="interest_on_pik_principal"
                checked={!!formik.values.interest_on_pik_principal}
                onChange={(e) => formik.setFieldValue("interest_on_pik_principal", e.target.checked)}
                disabled={disabledFields.includes("interest_on_pik_principal")}
              />
            }
            label={t("interestSection.interestOnPikPrincipal")}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
